/**
 * TAILWIND TOAST KOMPONENTE
 *
 * Toast-Benachrichtigungen mit Auto-Dismiss, Progress-Bar und Dark/Light Mode.
 *
 * Setup (einmalig in App.tsx):
 *   <TWToastProvider><App /></TWToastProvider>
 *
 * API (import { twToast } from '@/components/ui/TWToast'):
 *   twToast.success(title, message?, duration?)  - Grüner Toast (default 4s)
 *   twToast.error(title, message?, duration?)     - Roter Toast (default 5s)
 *   twToast.warning(title, message?, duration?)   - Gelber Toast (default 4s)
 *   twToast.info(title, message?, duration?)      - Blauer Toast (default 4s)
 *   twToast.loading(title, message?)              - Spinner-Toast (kein Auto-Dismiss)
 *   twToast.dismiss(id)                           - Toast manuell entfernen
 *   twToast.update(id, { type?, title?, ... })    - Toast aktualisieren
 *   twToast.promise(promise, { loading, success, error }) - Async-Toast
 *
 * Usage:
 *   twToast.success('Gespeichert!');
 *   twToast.error('Fehler', 'Details hier');
 *   twToast.promise(fetchData(), {
 *     loading: 'Wird geladen...',
 *     success: 'Fertig!',
 *     error: 'Fehlgeschlagen',
 *   });
 */

import React, { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';

// ============================================
// Types
// ============================================
type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  updateToast: (id: string, updates: Partial<Omit<Toast, 'id'>>) => void;
}

// ============================================
// Context
// ============================================
const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a TWToastProvider');
  }
  return context;
};

// ============================================
// Helper Functions (API wie react-hot-toast)
// ============================================
let toastFunctions: ToastContextType | null = null;

export const twToast = {
  success: (title: string, message?: string, duration?: number) => {
    return toastFunctions?.addToast({ type: 'success', title, message, duration: duration ?? 4000 }) || '';
  },
  error: (title: string, message?: string, duration?: number) => {
    return toastFunctions?.addToast({ type: 'error', title, message, duration: duration ?? 5000 }) || '';
  },
  warning: (title: string, message?: string, duration?: number) => {
    return toastFunctions?.addToast({ type: 'warning', title, message, duration: duration ?? 4000 }) || '';
  },
  info: (title: string, message?: string, duration?: number) => {
    return toastFunctions?.addToast({ type: 'info', title, message, duration: duration ?? 4000 }) || '';
  },
  loading: (title: string, message?: string) => {
    return toastFunctions?.addToast({ type: 'loading', title, message, duration: 0 }) || '';
  },
  dismiss: (id: string) => {
    toastFunctions?.removeToast(id);
  },
  update: (id: string, updates: Partial<Omit<Toast, 'id'>>) => {
    toastFunctions?.updateToast(id, updates);
  },
  promise: async <T,>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: unknown) => string);
    }
  ): Promise<T> => {
    const id = twToast.loading(options.loading);
    try {
      const result = await promise;
      const successMsg = typeof options.success === 'function'
        ? options.success(result)
        : options.success;
      toastFunctions?.updateToast(id, { type: 'success', title: successMsg, duration: 4000 });
      return result;
    } catch (err) {
      const errorMsg = typeof options.error === 'function'
        ? options.error(err)
        : options.error;
      toastFunctions?.updateToast(id, { type: 'error', title: errorMsg, duration: 5000 });
      throw err;
    }
  }
};

// Backward compatibility alias
export const rfToast = twToast;

// ============================================
// Toast Item Component
// ============================================
interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto-dismiss
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleRemove = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 150);
  }, [toast.id, onRemove]);

  const icons: Record<ToastType, ReactNode> = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <XCircle className="w-5 h-5 text-red-500" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
    loading: <Loader2 className="w-5 h-5 text-brand animate-spin" />,
  };

  return (
    <div
      className={clsx(
        'pointer-events-auto w-full max-w-sm rounded-lg shadow-lg transition-all duration-300 ease-out',
        // Light mode
        'bg-white border border-gray-200',
        // Dark mode
        'dark:bg-gray-800 dark:border-gray-700',
        // Animation states
        isVisible && !isLeaving
          ? 'translate-x-0 opacity-100'
          : 'translate-x-2 opacity-0 sm:translate-y-0',
        isLeaving && 'scale-95'
      )}
    >
      <div className="p-4">
        <div className="flex items-start">
          {/* Icon */}
          <div className="shrink-0">
            {icons[toast.type]}
          </div>

          {/* Content */}
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {toast.title}
            </p>
            {toast.message && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {toast.message}
              </p>
            )}
          </div>

          {/* Close Button */}
          {toast.type !== 'loading' && (
            <div className="ml-4 flex shrink-0">
              <button
                type="button"
                onClick={handleRemove}
                className={clsx(
                  'inline-flex rounded-md p-1 transition-colors',
                  'text-gray-400 hover:text-gray-600',
                  'dark:text-gray-500 dark:hover:text-gray-300',
                  'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2',
                  'dark:focus:ring-offset-gray-800'
                )}
              >
                <span className="sr-only">Schließen</span>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar für Auto-Dismiss */}
      {toast.duration !== undefined && toast.duration > 0 && toast.type !== 'loading' && (
        <div className="h-1 w-full bg-gray-100 dark:bg-gray-700 rounded-b-lg overflow-hidden">
          <div
            className={clsx(
              'h-full transition-all ease-linear',
              toast.type === 'success' && 'bg-green-500',
              toast.type === 'error' && 'bg-red-500',
              toast.type === 'warning' && 'bg-amber-500',
              toast.type === 'info' && 'bg-blue-500'
            )}
            style={{
              animation: `shrink ${toast.duration}ms linear forwards`
            }}
          />
        </div>
      )}
    </div>
  );
};

// ============================================
// Toast Container Component
// ============================================
const ToastContainer: React.FC<{ toasts: Toast[]; onRemove: (id: string) => void }> = ({
  toasts,
  onRemove
}) => {
  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[9999] flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-3 sm:items-end">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </div>

      {/* Keyframe Animation für Progress Bar */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// Toast Provider
// ============================================
interface TWToastProviderProps {
  children: ReactNode;
}

export const TWToastProvider: React.FC<TWToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const updateToast = useCallback((id: string, updates: Partial<Omit<Toast, 'id'>>) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
    );
  }, []);

  // Register global toast functions
  useEffect(() => {
    toastFunctions = { toasts, addToast, removeToast, updateToast };
    return () => {
      toastFunctions = null;
    };
  }, [toasts, addToast, removeToast, updateToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, updateToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Backward compatibility alias
export const ToastProvider = TWToastProvider;

// ============================================
// Default Export
// ============================================
export default twToast;
